import { useEffect, useMemo, useState } from 'react'
import { normalizeGuestCount, parseGuestCount } from '../../../../utils/guestCount'
import {
  Star,
  Bed,
  Bath,
  Users,
  Calendar,
  Pencil,
  Clock,
  AlertCircle
} from 'lucide-react'
import { getImageUrl } from '../../../../config/settings'
import DateRangePickerNew from '../DateRangePickerNew'
import GuestsModal from '../../../shared/GuestsModal'

interface TripStepProps {
  roomData: {
    title: string
    room_id: string | null
    featured_img: string | null
    rent: number
    rating: number
    bed: number
    bath: number
    max_guests: number
    category?: { name: string } | null
  }
  bookingData: {
    checkIn: string
    checkOut: string
    nights: number
    guests: string
  }
  errors: { [key: string]: string }
  onChange: (name: string, value: string) => void
}

const formatDate = (value: string) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : ''

const TripStep = ({ roomData, bookingData, errors, onChange }: TripStepProps) => {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showAdultsModal, setShowAdultsModal] = useState(false)

  const initialAdults = useMemo(
    () => parseGuestCount(bookingData.guests),
    [bookingData.guests]
  )

  // UI defaults to 1 adult — keep form state in sync so Next is enabled
  useEffect(() => {
    const normalized = normalizeGuestCount(bookingData.guests)
    if (bookingData.guests !== normalized) {
      onChange('guests', normalized)
    }
  }, [bookingData.guests, onChange])

  const counts = {
    adults: initialAdults,
    children: 0,
    infants: 0,
    pets: 0
  }

  const hasDates = !!(bookingData.checkIn && bookingData.checkOut)
  const dateError = errors.checkIn || errors.checkOut
  const guestError = errors.guests
  const adultsLabel = `${initialAdults} ${initialAdults === 1 ? 'Adult' : 'Adults'}`

  return (
    <div className="space-y-4">
      <div>
        <h5 className="text-sm sm:text-base text-gray-900">
          <span className="font-bold">Trip details</span>
          <span className="text-gray-600 font-normal"> — Confirm your room, dates and number of adults</span>
        </h5>
      </div>

      {/* Room card */}
      <div className="bg-white rounded-2xl shadow-sm p-2 sm:p-2 border border-gray-200">
        <div className="flex gap-3 sm:gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0">
            <img
              src={getImageUrl(roomData.featured_img || '')}
              alt={roomData.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-1.5 sm:mb-2 gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                  {roomData.title}
                </h4>
                <p className="text-[10px] sm:text-xs text-gray-500">
                  Room {roomData.room_id || 'N/A'}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg sm:text-xl font-bold text-primary-600">₹{roomData.rent}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">per night</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3 w-3 ${
                      i < Math.floor(roomData.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-600">{roomData.rating} Rating</span>
              {roomData.category && (
                <>
                  <span className="text-gray-300">•</span>
                  <span className="text-xs text-gray-600">{roomData.category.name}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
              <div className="flex items-center gap-1">
                <Bed className="h-3 w-3" />
                <span>{roomData.bed} Beds</span>
              </div>
              <div className="flex items-center gap-1">
                <Bath className="h-3 w-3" />
                <span>{roomData.bath} Bath</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                <span>Up to {roomData.max_guests} Guests</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Dates trigger */}
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => setShowDatePicker(true)}
            className={`w-full flex items-center gap-3 p-2 rounded-2xl border-2 text-left transition-colors ${
              dateError
                ? 'border-red-300 hover:border-red-400 bg-red-50/40'
                : hasDates
                  ? 'border-primary-300 bg-primary-50/40 hover:bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50/30'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
              <Calendar className="h-5 w-5 text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Check-in &amp; Check-out
              </div>
              {hasDates ? (
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {formatDate(bookingData.checkIn)} – {formatDate(bookingData.checkOut)}
                </div>
              ) : (
                <div className="text-sm text-gray-500">Tap to select your dates</div>
              )}
              {hasDates && bookingData.nights > 0 && (
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                  <Clock className="h-3 w-3" />
                  {bookingData.nights} {bookingData.nights === 1 ? 'night' : 'nights'}
                </div>
              )}
            </div>
            <Pencil className="h-4 w-4 text-gray-400 shrink-0" />
          </button>
          {dateError && (
            <p className="text-red-500 text-xs flex items-center gap-1 mt-1.5">
              <AlertCircle className="h-3.5 w-3.5" />
              {dateError}
            </p>
          )}
        </div>

        {/* Adults trigger */}
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => setShowAdultsModal(true)}
            className={`w-full flex items-center gap-3 p-2 rounded-2xl border-2 text-left transition-colors ${
              guestError
                ? 'border-red-300 hover:border-red-400 bg-red-50/40'
                : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50/30'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              {/* <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Adults
              </div> */}
              <div className="text-sm font-semibold text-gray-900 truncate">{adultsLabel}</div>
              {roomData.max_guests > 0 && (
                <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                  Up to {roomData.max_guests} adults for this room
                </div>
              )}
            </div>
            <Pencil className="h-4 w-4 text-gray-400 shrink-0" />
          </button>
          {guestError && (
            <p className="text-red-500 text-xs flex items-center gap-1 mt-1.5">
              <AlertCircle className="h-3.5 w-3.5" />
              {guestError}
            </p>
          )}
        </div>
      </div>

      {/* Dates modal (same as home) */}
      <DateRangePickerNew
        checkIn={bookingData.checkIn}
        checkOut={bookingData.checkOut}
        nights={bookingData.nights}
        errors={errors}
        onChange={onChange}
        showHeading={false}
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
      />

      {/* Adults modal (Adults-only mode) */}
      <GuestsModal
        isOpen={showAdultsModal}
        counts={counts}
        onChange={(next) => onChange('guests', String(next.adults || 1))}
        onClose={() => setShowAdultsModal(false)}
        show={['adults']}
        maxGuests={roomData.max_guests}
        title="How many adults?"
        subtitle="Choose the number of adults staying"
      />
    </div>
  )
}

export default TripStep
